import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Edit, Upload, Loader2, Shield } from "lucide-react";
import { toast } from "sonner";
import imageCompression from 'browser-image-compression';
import IssuerLayout from "@/components/IssuerLayout";
import CertificateEditor from "@/components/CertificateEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTemplates = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [systemTemplates, setSystemTemplates] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [backgroundPreview, setBackgroundPreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [templateLayout, setTemplateLayout] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    type: "certificate",
    background_base64: "",
    instructor_name: "Instructor Name",
    logo_base64: ""
  });

  useEffect(() => {
    // Check admin access
    const issuerData = localStorage.getItem('issuer_data');
    const token = localStorage.getItem('issuer_token');
    
    if (!issuerData || !token) {
      navigate("/issuer/login");
      return;
    }
    
    const data = JSON.parse(issuerData);
    if (data.email !== 'jdp@instructing.com') {
      toast.error("Unauthorized: Admin access only");
      navigate("/issuer/dashboard");
      return;
    }
    
    loadSystemTemplates();
  }, []);

  const loadSystemTemplates = async () => {
    try {
      const token = localStorage.getItem('issuer_token');
      const response = await axios.get(`${API}/admin/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSystemTemplates(response.data);
    } catch (err) {
      console.error("Failed to load system templates:", err);
      toast.error("Failed to load templates");
    }
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1056,
          useWebWorker: true
        };
        const compressedFile = await imageCompression(file, options);
        
        const reader = new FileReader();
        reader.onloadend = () => {
          setBackgroundPreview(reader.result);
          setFormData(prev => ({ ...prev, background_base64: reader.result }));
          toast.success(`Background uploaded: ${(compressedFile.size / 1024).toFixed(1)}KB`);
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        toast.error("Failed to upload background");
      }
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const options = {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 500,
          useWebWorker: true
        };
        const compressedFile = await imageCompression(file, options);
        
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoPreview(reader.result);
          setFormData(prev => ({ ...prev, logo_base64: reader.result }));
          toast.success(`Logo uploaded: ${(compressedFile.size / 1024).toFixed(1)}KB`);
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        toast.error("Failed to upload logo");
      }
    }
  };

  const handleSaveTemplate = (templateData) => {
    setTemplateLayout(templateData);
    toast.success("Template layout saved!");
  };

  const handleCreateNew = () => {
    setIsEditing(true);
    setEditingTemplate(null);
    setFormData({
      name: "",
      type: "certificate",
      background_base64: "",
      instructor_name: "Instructor Name",
      logo_base64: ""
    });
    setBackgroundPreview(null);
    setLogoPreview(null);
    setTemplateLayout(null);
  };

  const handleEdit = (template) => {
    setIsEditing(true);
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      type: template.type,
      background_base64: template.background_url || "",
      instructor_name: template.instructor_name || "Instructor Name",
      logo_base64: template.logo_base64 || ""
    });
    setBackgroundPreview(template.background_url);
    setLogoPreview(template.logo_base64);
    setTemplateLayout(template.layout);
  };

  const handleSaveSystemTemplate = async () => {
    if (!formData.name) {
      toast.error("Please enter a template name");
      return;
    }
    
    if (!backgroundPreview) {
      toast.error("Please upload a background image");
      return;
    }
    
    if (!templateLayout) {
      toast.error("Please save the template layout first");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('issuer_token');
      const templateData = {
        name: formData.name,
        type: formData.type,
        background_url: formData.background_base64,
        layout: templateLayout,
        instructor_name: formData.instructor_name,
        logo_base64: formData.logo_base64
      };

      if (editingTemplate) {
        // Update existing template
        await axios.put(
          `${API}/admin/templates/${editingTemplate.id}`,
          templateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("System template updated!");
      } else {
        // Create new template
        await axios.post(
          `${API}/admin/templates`,
          templateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("System template created!");
      }

      await loadSystemTemplates();
      setIsEditing(false);
    } catch (err) {
      console.error("Failed to save template:", err);
      toast.error(err.response?.data?.detail || "Failed to save template");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!confirm("Are you sure you want to delete this system template?")) return;
    
    try {
      const token = localStorage.getItem('issuer_token');
      await axios.delete(`${API}/admin/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Template deleted");
      await loadSystemTemplates();
    } catch (err) {
      console.error("Failed to delete template:", err);
      toast.error(err.response?.data?.detail || "Failed to delete template");
    }
  };

  if (isEditing) {
    return (
      <IssuerLayout>
        <div data-testid="admin-template-editor">
          <div className="mb-8">
            <h1 className="text-4xl font-medium text-slate-900 mb-2" style={{fontFamily: 'Playfair Display'}}>
              <Shield className="inline h-8 w-8 mr-2 text-amber-600" />
              Admin: {editingTemplate ? 'Edit' : 'Create'} System Template
            </h1>
            <p className="text-slate-600">Design templates that will be available to all users</p>
          </div>

          {/* Template Settings */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Template Information</h2>
            
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label htmlFor="template-name">Template Name</Label>
                <Input
                  id="template-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Gold Seal Professional"
                  className="h-11 rounded-lg"
                />
              </div>

              <div>
                <Label htmlFor="template-type">Type</Label>
                <select
                  id="template-type"
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full h-11 rounded-lg border border-slate-200 px-3"
                >
                  <option value="certificate">Certificate</option>
                </select>
              </div>
            </div>

            <div className="mb-4">
              <Label htmlFor="instructor-name">Default Instructor Name (Placeholder)</Label>
              <Input
                id="instructor-name"
                type="text"
                value={formData.instructor_name}
                onChange={(e) => setFormData({...formData, instructor_name: e.target.value})}
                placeholder="e.g., [Instructor Name]"
                className="h-11 rounded-lg"
              />
              <p className="text-xs text-slate-500 mt-1">This is a placeholder - issuers will fill in the actual name when creating certificates</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bg-upload">Background Image (Required)</Label>
                <label className="cursor-pointer block">
                  <input
                    id="bg-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleBackgroundUpload}
                    className="hidden"
                  />
                  <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 h-11">
                    <Upload className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-blue-700 font-medium">
                      {backgroundPreview ? 'Change Background' : 'Upload Background'}
                    </span>
                  </div>
                </label>
                {backgroundPreview && (
                  <div className="mt-2 h-20 border border-slate-200 rounded-lg overflow-hidden">
                    <img src={backgroundPreview} alt="Background" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="logo-upload">Default Logo (Optional)</Label>
                <label className="cursor-pointer block">
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg border border-slate-200 h-11">
                    <Upload className="h-4 w-4 text-slate-600" />
                    <span className="text-sm text-slate-700">
                      {logoPreview ? 'Change Logo' : 'Upload Logo'}
                    </span>
                  </div>
                </label>
                {logoPreview && (
                  <div className="mt-2 h-20 w-20 border border-slate-200 rounded-lg overflow-hidden bg-white flex items-center justify-center">
                    <img src={logoPreview} alt="Logo" className="max-h-full max-w-full object-contain" />
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleSaveSystemTemplate}
                disabled={loading}
                className="bg-amber-600 text-white hover:bg-amber-700 rounded-lg px-6 py-2"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save System Template
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsEditing(false)}
                className="rounded-lg px-6 py-2"
              >
                Cancel
              </Button>
            </div>
          </div>

          {/* Certificate Editor */}
          {backgroundPreview && (
            <CertificateEditor
              templateBackground={
                backgroundPreview.startsWith('data:') 
                  ? backgroundPreview 
                  : `${API}/proxy-image?url=${encodeURIComponent(backgroundPreview)}`
              }
              onSave={handleSaveTemplate}
              logoPreview={logoPreview}
              instructorName={formData.instructor_name}
              isCustomBackground={true}
              initialLayout={templateLayout}
            />
          )}
        </div>
      </IssuerLayout>
    );
  }

  return (
    <IssuerLayout>
      <div data-testid="admin-templates-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-medium text-slate-900 mb-2" style={{fontFamily: 'Playfair Display'}}>
              <Shield className="inline h-8 w-8 mr-2 text-amber-600" />
              Admin: System Templates
            </h1>
            <p className="text-slate-600">Manage templates available to all users</p>
          </div>
          <Button
            onClick={handleCreateNew}
            className="bg-amber-600 text-white hover:bg-amber-700 rounded-lg px-6 py-3"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create New Template
          </Button>
        </div>

        {/* Admin Info */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-900 mb-2">Admin Access</h3>
              <p className="text-sm text-amber-800">
                You have admin privileges to create and manage system-wide templates. Templates you create here will appear as preset options for all users when they create certificates.
              </p>
            </div>
          </div>
        </div>

        {/* Templates List */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          {systemTemplates.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-slate-500 mb-4">No system templates yet</p>
              <Button onClick={handleCreateNew} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Create First Template
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {systemTemplates.map((template) => (
                <div key={template.id} className="border border-slate-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="h-40 bg-slate-100 overflow-hidden">
                    <img 
                      src={template.background_url} 
                      alt={template.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-1">{template.name}</h3>
                    <p className="text-xs text-slate-500 mb-3 capitalize">{template.type}</p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleEdit(template)}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleDelete(template.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </IssuerLayout>
  );
};

export default AdminTemplates;
